import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import {
  doc,
  getDoc,
  collection,
  addDoc,
  serverTimestamp,
  onSnapshot,
  query,
  orderBy,
  updateDoc,
  where,
  limit,
  getDocs
} from 'firebase/firestore';

function PostDetail({ user, setShowLogin }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState('');

  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [showReplyBox, setShowReplyBox] = useState(false);

  const [usernames, setUsernames] = useState({});

  // Banner messages for U6/U7 and R3/R6/R7/R11
  const [banner, setBanner] = useState({ type: '', text: '' });

  // V5/V6 vs V7/V8/V9 unlock logic
  const [hasContributed, setHasContributed] = useState(false);
  const [contribLoading, setContribLoading] = useState(false);

  const MAX_REPLY_LENGTH = 500;
  const REPLY_DRAFT_KEY = `replyDraft_${id}`;

  useEffect(() => {
    const postRef = doc(db, 'posts', id);

    const unsubscribe = onSnapshot(
      postRef,
      (snap) => {
        if (snap.exists()) {
          setPost({ id: snap.id, ...snap.data() });
          setPageError('');
        } else {
          setPost(null);
          setPageError('Post not found.');
        }
        setLoading(false);
      },
      () => {
        setPageError('Error loading post.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [id]);

  // ------------Subscribe to replies (newest first) (R2/R4)--------
  useEffect(() => {
    const repliesRef = collection(db, 'posts', id, 'replies');
    const q = query(repliesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const replyDocs = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data()
        }));
        setReplies(replyDocs);
      },
      () => {
        setReplies([]);
      }
    );

    return () => unsubscribe();
  }, [id]);

  // -----------Persist reply draft--------------
  useEffect(() => {
    const saved = localStorage.getItem(REPLY_DRAFT_KEY);
    if (saved != null) {
      setReplyText(saved);
    }
  }, [REPLY_DRAFT_KEY]);

  useEffect(() => {
    localStorage.setItem(REPLY_DRAFT_KEY, replyText);
  }, [REPLY_DRAFT_KEY, replyText]);

  // --------------Fetch usernames for replies R5--------
  useEffect(() => {
    async function fetchUsernames() {
      const uniqueUserIds = [...new Set(replies.map((r) => r.userId))];

      for (const uid of uniqueUserIds) {
        if (!uid) continue;

        if (!usernames[uid]) {
          try {
            const userRef = doc(db, 'users', uid);
            const snap = await getDoc(userRef);

            const name =
              snap.exists() && snap.data().username
                ? snap.data().username
                : 'Anonymous';

            setUsernames((prev) => ({ ...prev, [uid]: name }));
          } catch {
            setUsernames((prev) => ({ ...prev, [uid]: 'Anonymous' }));
          }
        }
      }
    }

    fetchUsernames();
  }, [replies, usernames]);

  // Determine if logged-in user has contributed V5–V9
  useEffect(() => {
    async function checkContribution() {
      if (!user) {
        setHasContributed(false);
        return;
      }

      setContribLoading(true);
      try {
        const postsRef = collection(db, 'posts');
        const q = query(postsRef, where('userId', '==', user.uid), limit(1));
        const snap = await getDocs(q);
        setHasContributed(!snap.empty);
      } catch (err) {
        setHasContributed(false);
      } finally {
        setContribLoading(false);
      }
    }

    checkContribution();
  }, [user]);

  // Reply actions
  const handleReplyClick = () => {
    setBanner({ type: '', text: '' });

    // R8: logged-out users should not see reply button
    if (!user) return;

    if (!hasContributed) {
    setBanner({
      type: 'error',
      text: 'To reply, please post your own interview experience first to unlock full access.'
    });
    return;
  }
  
    setShowReplyBox((prev) => !prev);
  };

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    setBanner({ type: '', text: '' });

    const clean = replyText.trim();

    // R6
    if (!clean) {
      setBanner({ type: 'error', text: 'Please enter text before submitting your reply.' });
      return;
    }

    // R7
    if (clean.length > MAX_REPLY_LENGTH) {
      setBanner({
        type: 'error',
        text: 'Your reply is too long (max 500 characters). Please shorten your text and try again.'
      });
      return;
    }

    // R1: user must be logged in
    if (!user) {
      setBanner({ type: 'error', text: 'Please log in or sign up to reply.' });
      if (setShowLogin) setShowLogin(true);
      return;
    }

    if (!hasContributed) {
      setBanner({
        type: 'error',
        text: 'Please post your own interview experience first to unlock replying.'
      });
      return;
    }

    try {
      const repliesRef = collection(db, 'posts', id, 'replies');

      await addDoc(repliesRef, {
        text: clean,
        userId: user.uid,
        createdAt: serverTimestamp()
      });

      // R3
      setBanner({ type: 'success', text: 'Reply submitted successfully!' });

      setReplyText('');
      localStorage.removeItem(REPLY_DRAFT_KEY);
      setShowReplyBox(false);
    } catch (err) {
      // R11
      setBanner({
        type: 'error',
        text: 'Something went wrong. Please try clicking the Reply button again.'
      });
    }
  };

  // ------Upvote actions (U)-------------
  const handleUpvote = async () => {
    setBanner({ type: '', text: '' });

    // U6: user not logged in
    if (!user) {
      setBanner({ type: 'error', text: 'Please log in to upvote.' });
      if (setShowLogin) setShowLogin(true);
      return;
    }

    if (!hasContributed) {
      setBanner({
        type: 'error',
        text: 'Please post your own interview experience first to unlock upvoting.'
      });
      return;
    }

    if (!post || !post.id) return;
    if (user.uid === post.userId) return;

    const upvotedBy = post.upvotedBy || [];
    if (upvotedBy.includes(user.uid)) return;

    try {
      const postRef = doc(db, 'posts', post.id);
      await updateDoc(postRef, {
        upvotes: (post.upvotes ?? 0) + 1,
        upvotedBy: [...upvotedBy, user.uid]
      });

      // U3: update immediately
      setPost((prev) => ({
        ...prev,
        upvotes: (prev.upvotes ?? 0) + 1,
        upvotedBy: [...(prev.upvotedBy || []), user.uid]
      }));
    } catch (err) {
      // U7
      setBanner({ type: 'error', text: 'Your upvote could not be saved. Please try again.' });
    }
  };

  const handleDownvote = async () => {
    setBanner({ type: '', text: '' });

    if (!user) {
      setBanner({ type: 'error', text: 'Please log in to upvote.' });
      if (setShowLogin) setShowLogin(true);
      return;
    }

    if (!post || !post.id) return;
    if (user.uid === post.userId) return;

    const upvotedBy = post.upvotedBy || [];
    if (!upvotedBy.includes(user.uid)) return;

    try {
      const postRef = doc(db, 'posts', post.id);
      await updateDoc(postRef, {
        upvotes: Math.max((post.upvotes ?? 1) - 1, 0),
        upvotedBy: upvotedBy.filter((uid) => uid !== user.uid)
      });

      setPost((prev) => ({
        ...prev,
        upvotes: Math.max((prev.upvotes ?? 1) - 1, 0),
        upvotedBy: (prev.upvotedBy || []).filter((uid) => uid !== user.uid)
      }));
    } catch (err) {
      setBanner({ type: 'error', text: 'Your upvote could not be saved. Please try again.' });
    }
  };

  if (loading) return <div className='loading-state'>Loading...</div>;
  if (pageError) return <div className='error-state'>{pageError}</div>;
  if (!post) return null;

  // V rules
  const showFullDetails = !!user && hasContributed;
  const showLimitedPreview = !user;
  const showOverviewOnlyForLoggedInNoPosts = !!user && !hasContributed;

  return (
    <div className='post-detail-page'>
      <div className='post-detail-wrapper'>
        {/* Banner */}
        {banner.text && (
          <div
            style={{
              background: banner.type === 'success' ? '#e7f6ea' : '#fde8e8',
              border: banner.type === 'success' ? '1px solid #9fd3a7' : '1px solid #f5b5b5',
              color: '#133250',
              padding: '12px 14px',
              borderRadius: '8px',
              marginBottom: '12px',
              fontWeight: 600
            }}
          >
            {banner.text}
          </div>
        )}

        <button
          className='back-button'
          onClick={() => {
            const from = location.state && location.state.from;
            if (from === '/search') {
              navigate('/search');
            } else if (from === '/post') {
              navigate('/post');
            } else {
              navigate('/');
            }
          }}
        >
          [back]
        </button>

        <div className='post-card'>
          <div className='post-content-main'>
            {/* Title */}
            <div className='post-header-detail'>
              <div className='post-title'>
                {post.roleName} at {post.companyName}
              </div>

              <div className='post-meta-info'>
                <span>Rounds: {post.numRounds}</span>
                <span className='meta-dot'>•</span>
                <span>Rating: {post.rating}</span>
                <span className='meta-dot'>•</span>
                <span>Offer: {post.offerReceived ? 'Yes' : 'No'}</span>
              </div>
            </div>

            {/* U1: upvote always shown with count */}
            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                className='action-btn'
                onClick={handleUpvote}
                title={!user ? 'Log in to upvote' : ''}
                disabled={user ? (user.uid === post.userId || (post.upvotedBy || []).includes(user.uid)) : false}
              >
                ▲
              </button>

              <span style={{ fontWeight: 700 }}>{post.upvotes ?? 0}</span>

              <button
                className='action-btn'
                onClick={handleDownvote}
                disabled={
                  !user ||
                  user.uid === post.userId ||
                  !(post.upvotedBy || []).includes(user.uid) ||
                  (post.upvotes ?? 0) === 0
                }
                title={!user ? 'Log in to downvote' : ''}
              >
                ▼
              </button>
            </div>

            {/* V1–V4: Overview */}
            <div className='interview-overview-section'>
              <h3>Overview</h3>
              <div className='overview-tags'>
                <span className='tag'>{post.companyName}</span>
                <span className='tag'>{post.roleName}</span>
                <span className='tag'>Rounds: {post.numRounds}</span>
                <span className='tag'>Rating: {post.rating}</span>
              </div>
            </div>

            {/* V10-V11: unauthenticated user limited preview */}
            {showLimitedPreview && (
              <div style={{ marginTop: '10px', color: '#666' }}>
                <p style={{ margin: '8px 0' }}>
                  You are viewing a limited preview. Log in and share your own experience to unlock full interview details.
                </p>
                <button
                  className='action-btn'
                  onClick={() => {
                    if (setShowLogin) setShowLogin(true);
                  }}
                >
                  [log in to unlock]
                </button>
              </div>
            )}

            {/* V7–V9: Logged-in but no contributed posts */}
            {showOverviewOnlyForLoggedInNoPosts && (
              <div style={{ marginTop: '10px', color: '#666' }}>
                {contribLoading ? (
                  <p>Checking your access...</p>
                ) : (
                  <p>
                    You can see the overview. Post your own interview experience to unlock full interview details and questions.
                  </p>
                )}
              </div>
            )}

            {/* V5-V6: full interview details if contributed */}
            {showFullDetails && (
              <>
                <div className='questions-section'>
                  <h3>Interview Details</h3>
                  <div className='question-content'>
                    <p><strong>Timeline:</strong> {post.timeline || 'N/A'}</p>
                    <p><strong>Offer:</strong> {post.offerReceived ? 'Yes' : 'No'}</p>
                  </div>
                </div>

                <div className='questions-section'>
                  <h3>Behavioral Questions</h3>
                  <div className='question-content'>
                    <p>{post.behavioralQuestions || 'No behavioral questions provided.'}</p>
                  </div>
                </div>

                <div className='questions-section'>
                  <h3>Technical Questions</h3>
                  <div className='question-content'>
                    <p>{post.technicalQuestions || 'No technical questions provided.'}</p>
                  </div>
                </div>
              </>
            )}

            {/* R8: hide reply button when not logged in */}
            {user && (
              <>
                <div className='post-actions'>
                  <button className='action-btn' onClick={handleReplyClick}>
                    Reply
                  </button>
                  <span className='action-btn' style={{ cursor: 'default' }}>
                    {replies.length} repl{replies.length === 1 ? 'y' : 'ies'}
                  </span>
                </div>

                {showReplyBox && (
                  <form onSubmit={handleSubmitReply} className='comment-form'>
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder='Write your reply...'
                      rows='3'
                    />
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                      {replyText.trim().length}/{MAX_REPLY_LENGTH}
                    </div>
                    <button type='submit'>[reply]</button>
                  </form>
                )}
              </>
            )}
          </div>
        </div>

        <div className='comments-section'>
          <h3>Replies (newest first)</h3>

          {replies.length === 0 ? (
            <p style={{ color: '#666' }}>No replies yet.</p>
          ) : (
            <div className='comments-list'>
              {replies.map((r) => (
                <div key={r.id} className='comment'>
                  <div className='comment-header'>
                    <span className='comment-author'>
                      {usernames[r.userId] || 'Anonymous'}
                    </span>
                  </div>
                  <p className='comment-text'>{r.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default PostDetail;